/**
 * Frontend Report Service
 *
 * API client for the user reporting system.
 * Wraps apiClient to handle FormData for screenshot uploads.
 *
 * @module services/reportService (frontend)
 */

import apiClient from './apiClient';

/**
 * Submits a new user report.
 * Handles optional screenshot blob via FormData.
 *
 * @param {Object} payload
 * @param {string} payload.severity - 'info'|'bug'|'blocker'
 * @param {string} payload.area
 * @param {string} payload.title
 * @param {string} [payload.description]
 * @param {Object} [payload.context]
 * @param {boolean} [payload.consentGiven]
 * @param {Blob|null} [payload.screenshotBlob] - Optional screenshot blob
 * @returns {Promise<Object>} Created report
 */
export async function submitReport({
  severity,
  area,
  title,
  description,
  context,
  consentGiven = false,
  screenshotBlob = null,
}) {
  // If screenshot is provided, use FormData so the file upload works
  if (screenshotBlob && consentGiven) {
    const formData = new FormData();
    formData.append('severity', severity);
    formData.append('area', area);
    formData.append('title', title);
    if (description) formData.append('description', description);
    if (context) formData.append('context', JSON.stringify(context));
    formData.append('consentGiven', String(consentGiven));
    formData.append('screenshot', screenshotBlob, 'screenshot.png');

    return apiClient.post('/api/reports', formData);
  }

  // JSON payload (no screenshot)
  return apiClient.post('/api/reports', {
    severity,
    area,
    title,
    description,
    context,
    consentGiven,
  });
}

/**
 * Lists the authenticated user's OWN reports (status-tracking view).
 * Scoped server-side to the caller; no admin role required.
 *
 * @param {Object} [opts]
 * @param {number} [opts.page=1]
 * @param {number} [opts.limit=50]
 * @returns {Promise<{items: Array, total: number, page: number, limit: number}>}
 */
export async function listMyReports({ page, limit } = {}) {
  const params = new URLSearchParams();
  if (page) params.set('page', String(page));
  if (limit) params.set('limit', String(limit));
  const qs = params.toString();
  return apiClient.get(`/api/reports/mine${qs ? `?${qs}` : ''}`);
}

/**
 * Lists reports with optional filters. Admin only.
 *
 * @param {Object} [filters]
 * @param {string} [filters.severity]
 * @param {string} [filters.area]
 * @param {string} [filters.status]
 * @param {string} [filters.userId]
 * @param {string} [filters.fromDate]
 * @param {string} [filters.toDate]
 * @param {number} [filters.page=1]
 * @param {number} [filters.limit=25]
 * @returns {Promise<{items: Array, total: number, page: number, limit: number}>}
 */
export async function listReports(filters = {}) {
  const params = new URLSearchParams();
  if (filters.severity) params.set('severity', filters.severity);
  if (filters.area) params.set('area', filters.area);
  if (filters.status) params.set('status', filters.status);
  if (filters.userId) params.set('userId', filters.userId);
  if (filters.fromDate) params.set('fromDate', filters.fromDate);
  if (filters.toDate) params.set('toDate', filters.toDate);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));

  const qs = params.toString();
  return apiClient.get(`/api/reports${qs ? `?${qs}` : ''}`);
}

/**
 * Gets a single report by ID.
 *
 * @param {string} id - Report UUID
 * @returns {Promise<Object>} Report detail
 */
export async function getReportDetail(id) {
  return apiClient.get(`/api/reports/${id}`);
}

/**
 * Partially updates a report.
 *
 * @param {string} id - Report UUID
 * @param {Object} updates
 * @param {string} [updates.status]
 * @param {string|null} [updates.assignee]
 * @param {string|null} [updates.resolutionNotes]
 * @param {string|null} [updates.resolutionPublicMessage]
 * @param {string} [updates.expectedStatus] - el estado CARGADO: si el reporte ya no está en él
 *   (p. ej. el usuario lo retiró), el backend responde 409 `REPORT_STATUS_CHANGED` sin escribir
 * @returns {Promise<Object>} Updated report
 */
export async function updateReport(id, updates) {
  return apiClient.patch(`/api/reports/${id}`, updates);
}

/**
 * Permanently deletes a report (DB row + S3 screenshot). Admin only.
 * Irreversible — also removes it from the reporter's "Mis incidencias" view.
 *
 * @param {string} id - Report UUID
 * @returns {Promise<{id: string, deleted: boolean}>}
 */
export async function deleteReport(id) {
  return apiClient.delete(`/api/reports/${id}`);
}

/**
 * Fetches the Markdown export for a report.
 *
 * @param {string} id - Report UUID
 * @returns {Promise<string>} Markdown string
 */
export async function fetchExportMarkdown(id) {
  // Raw fetch needed since the response is text/markdown, not JSON
  const { default: authService } = await import('./authService');
  const { default: config } = await import('../config');
  const token = await authService.getToken();

  const res = await fetch(`${config.apiUrl}/api/reports/${id}/export.md`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    throw new Error(`Failed to fetch markdown export: ${res.status}`);
  }

  return res.text();
}

// ─────────────────────────────────────────────────────────────────
// Notification recipients CRUD (admin)
// ─────────────────────────────────────────────────────────────────

export async function listRecipients() {
  return apiClient.get('/api/reports/recipients');
}

export async function createRecipient({ email, enabled = true, minSeverity = 'bug', notes = null }) {
  return apiClient.post('/api/reports/recipients', { email, enabled, minSeverity, notes });
}

export async function updateRecipient(id, patch) {
  return apiClient.patch(`/api/reports/recipients/${id}`, patch);
}

export async function deleteRecipient(id) {
  return apiClient.delete(`/api/reports/recipients/${id}`);
}
